/**
 * <collection-facets data-section-id="…">
 * Filters, sort, active-filter chips, pagination and "load more" for the
 * collection page through the Section Rendering API. Every control is a
 * plain form or link first; this element only makes it happen in place.
 *
 *  [data-facets-form]        forms applied on input (debounced) and submit
 *  [data-facets-sort]        select that changes sort_by
 *  [data-facets-link]        links loaded in place (chips, reset, pagination)
 *  [data-load-more-button]   appends the next page to [data-facets-grid]
 *  [data-facets-swap="name"] regions replaced from the fetched section
 *
 * The toolbar sticks under the header: --plp-toolbar-top follows the
 * header's visible bottom edge so sticky, compact and hidden headers all
 * line up without knowing how the header behaves.
 */
/* <price-range>: two overlapping range inputs plus min/max fields kept in
 * sync; --range-min/--range-max paint the selected span on the track. */
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.max = Number(this.dataset.max) || 0;
    this.ranges = { min: this.querySelector('[data-price-range="min"]'), max: this.querySelector('[data-price-range="max"]') };
    this.fields = { min: this.querySelector('[data-price-field="min"]'), max: this.querySelector('[data-price-field="max"]') };
    if (!this.ranges.min || !this.ranges.max) return;
    ['min', 'max'].forEach((key) => {
      this.ranges[key].addEventListener('input', () => this.fromRange(key));
      this.fields[key]?.addEventListener('input', () => this.fromField(key));
    });
    this.paint();
  }

  fromRange(key) {
    let min = Number(this.ranges.min.value);
    let max = Number(this.ranges.max.value);
    if (key === 'min' && min > max) { min = max; this.ranges.min.value = min; }
    if (key === 'max' && max < min) { max = min; this.ranges.max.value = max; }
    if (this.fields.min) this.fields.min.value = min > 0 ? min : '';
    if (this.fields.max) this.fields.max.value = max < this.max ? max : '';
    this.paint();
  }

  fromField(key) {
    const value = Number(this.fields[key]?.value);
    if (Number.isFinite(value)) this.ranges[key].value = Math.max(0, Math.min(this.max, value || (key === 'max' ? this.max : 0)));
    this.paint();
  }

  paint() {
    if (!this.max) return;
    this.style.setProperty('--range-min', `${(Number(this.ranges.min.value) / this.max) * 100}%`);
    this.style.setProperty('--range-max', `${(Number(this.ranges.max.value) / this.max) * 100}%`);
  }
}

if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class CollectionFacets extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.status = this.querySelector('[data-facets-status]');
    this.controller = null;
    this.timer = null;
    this.stockOrder = window.CasamobStockOrder ? new window.CasamobStockOrder(this.sectionId) : null;

    this.addEventListener('input', (event) => {
      /* In-list search filters rows locally and never touches the form. */
      const search = event.target.closest('[data-facet-search]');
      if (search) {
        this.filterList(search);
        return;
      }
      if (event.target.type === 'range') return;
      const form = event.target.closest('[data-facets-form]');
      if (!form) return;
      const delay = event.target.type === 'number' ? 600 : 120;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.load(this.urlFromForm(form)), delay);
    });

    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-facets-form]');
      if (!form) return;
      event.preventDefault();
      window.clearTimeout(this.timer);
      this.load(this.urlFromForm(form));
    });

    /* Price slider: a released thumb applies the range. */
    this.addEventListener('change', (event) => {
      if (event.target.type !== 'range' || !event.target.closest('[data-facets-form]')) return;
      window.clearTimeout(this.timer);
      this.load(this.urlFromForm(event.target.closest('[data-facets-form]')));
    });

    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-facets-sort]')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      this.load(url.pathname + url.search);
    });

    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facets-link]');
      if (link) {
        event.preventDefault();
        this.load(link.getAttribute('href'), { scroll: link.hasAttribute('data-facets-scroll') });
        return;
      }
      const more = event.target.closest('[data-load-more-button]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    });

    // Bar mode: only one dropdown open at a time, and outside clicks close it.
    document.addEventListener('click', (event) => {
      this.querySelectorAll('.facets--bar details[open]').forEach((details) => {
        if (!details.contains(event.target)) details.open = false;
      });
    });

    this.bindToolbarOffset();
    if (this.stockOrder && this.querySelector('[data-stock-page]')) this.initializeStockOrder();
    else this.bindAutoLoad();
  }

  async initializeStockOrder() {
    this.controller = new AbortController();
    const controller = this.controller;
    this.setAttribute('data-stock-initializing', '');
    this.setAttribute('aria-busy', 'true');
    try {
      await this.orderStock(this, window.location.href, controller.signal);
      this.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));
    } finally {
      this.removeAttribute('data-stock-initializing');
      if (this.controller === controller) {
        this.removeAttribute('aria-busy');
        this.bindAutoLoad();
      }
    }
  }

  async orderStock(doc, url, signal) {
    if (!this.stockOrder) return doc;
    const deadline = new AbortController();
    const abort = () => deadline.abort();
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    const timeout = window.setTimeout(abort, 15000);
    try {
      return await this.stockOrder.prepare(doc, url, deadline.signal);
    } catch (error) {
      if (signal.aborted) return doc;
      // Keep the complete native collection if an auxiliary request fails.
      console.warn('Collection stock ordering unavailable:', error.message);
      return doc;
    } finally {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
  }

  /* Infinite scroll (Theme settings → Collection → Pagination): the next page
   * loads when the load-more control scrolls near the viewport. */
  bindAutoLoad() {
    this.autoObserver?.disconnect();
    const button = this.querySelector('[data-load-more-auto] [data-load-more-button]');
    if (!button || !('IntersectionObserver' in window)) return;
    this.autoObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.autoObserver.disconnect();
        if (!button.hasAttribute('aria-busy')) this.loadMore(button);
      },
      { rootMargin: '0px 0px 60% 0px' }
    );
    this.autoObserver.observe(button);
  }

  filterList(input) {
    const body = input.closest('.facet__body');
    if (!body) return;
    const needle = input.value.trim().toLowerCase();
    let shown = 0;
    body.querySelectorAll('[data-facet-item]').forEach((item) => {
      const match = !needle || item.textContent.toLowerCase().includes(needle);
      item.hidden = !match;
      if (match) shown += 1;
    });
    const empty = body.querySelector('[data-facet-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  urlFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const query = params.toString();
    return window.location.pathname + (query ? `?${query}` : '');
  }

  async fetchSection(url) {
    const signal = this.controller.signal;
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(this.sectionId)}`, {
      signal,
    });
    if (!response.ok) throw new Error(`Section request failed (${response.status})`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    await this.orderStock(doc, url, signal);
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    return doc;
  }

  async load(url, { scroll = false } = {}) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.fetchSection(url);
      this.swap(doc);
      window.history.replaceState({}, '', url);
      if (scroll) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
    }
  }

  swap(doc) {
    const active = document.activeElement;
    const focusId = active && this.contains(active) ? active.id : '';
    const openState = new Map();
    this.querySelectorAll('[data-facets-swap] details[data-facet]').forEach((details) => {
      openState.set(this.facetKey(details), details.open);
    });

    this.querySelectorAll('[data-facets-swap]').forEach((slot) => {
      const next = doc.querySelector(`[data-facets-swap="${slot.dataset.facetsSwap}"]`);
      if (next) slot.innerHTML = next.innerHTML;
    });

    this.querySelectorAll('[data-facets-swap] details[data-facet]').forEach((details) => {
      const key = this.facetKey(details);
      if (openState.has(key)) details.open = openState.get(key);
    });
    if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });

    const count = this.querySelector('[data-facets-count]');
    if (this.status && count) this.status.textContent = count.textContent.trim();
    // New grid nodes join the scroll-reveal choreography (theme.js).
    this.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));
    this.bindAutoLoad();
  }

  facetKey(details) {
    return `${details.closest('[data-facets-swap]')?.dataset.facetsSwap}|${details.dataset.facet}`;
  }

  async loadMore(button) {
    const url = button.getAttribute('href');
    button.setAttribute('aria-busy', 'true');
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const doc = await this.fetchSection(url);
      const grid = this.querySelector('[data-facets-grid]');
      const nextGrid = doc.querySelector('[data-facets-grid]');
      const items = nextGrid && grid ? Array.from(nextGrid.children) : [];
      items.forEach((item) => grid.appendChild(item));
      grid.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));

      const current = this.querySelector('[data-load-more]');
      const next = doc.querySelector('[data-load-more]');
      if (current && next) current.replaceWith(next);
      else current?.remove();

      items[0]?.querySelector('a, button')?.focus({ preventScroll: true });
      const shown = this.querySelector('[data-load-more-shown]');
      if (this.status && shown) this.status.textContent = shown.textContent.trim();
      this.bindAutoLoad();
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
    }
  }

  bindToolbarOffset() {
    const header = document.querySelector('.section-header');
    if (!header) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      this.style.setProperty('--plp-toolbar-top', `${bottom}px`);
    };
    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    header.addEventListener('transitionend', request);
  }
}

if (!customElements.get('collection-facets')) customElements.define('collection-facets', CollectionFacets);
